import { useEffect, useMemo, useState } from "react";
import { deleteField, doc, onSnapshot, runTransaction, serverTimestamp } from "firebase/firestore";
import toast from "react-hot-toast";
import { db } from "../../firebase";
import OmiCreateRoomModal from "./OmiCreateRoomModal";
import OmiGameRoom from "./OmiGameRoom";
import OmiJoinRoomModal from "./OmiJoinRoomModal";
import OmiRoomWaiting from "./OmiRoomWaiting";
import { getOpposingTeam, getTeamKeyFromSeat } from "./omiUtils";
import { useOmiRoom } from "./useOmiRoom";

export default function OmiLobbyPage({ user }) {
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [currentRoomId, setCurrentRoomId] = useState(null);
  const [balance, setBalance] = useState(0);
  const room = useOmiRoom(currentRoomId);

  useEffect(() => {
    if (!user?.uid) return undefined;
    return onSnapshot(doc(db, "users", user.uid), (snapshot) => {
      const userData = snapshot.data() || {};
      setBalance(Number(userData.balance || 0));
      const activeRoomId = userData.activeOmiRoomId || null;
      if (activeRoomId) {
        setCurrentRoomId((current) => current || activeRoomId);
        return;
      }
      const storedRoomId = window.localStorage.getItem(`omi-room:${user.uid}`);
      if (storedRoomId) {
        setCurrentRoomId((current) => current || storedRoomId);
      }
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!currentRoomId || room) return;
    setCurrentRoomId(null);
  }, [currentRoomId, room]);

  useEffect(() => {
    if (!user?.uid) return;
    const storageKey = `omi-room:${user.uid}`;
    if (currentRoomId) {
      window.localStorage.setItem(storageKey, currentRoomId);
    } else {
      window.localStorage.removeItem(storageKey);
    }
  }, [currentRoomId, user?.uid]);

  useEffect(() => {
    if (!room || !user?.uid) return;
    const isMember = (room.players || []).some((player) => player.userId === user.uid);
    if (!isMember) {
      setCurrentRoomId(null);
    }
  }, [room, user?.uid]);

  const isPlaying = room?.status === "playing" || room?.status === "finished";

  const summaryStats = useMemo(
    () => [
      { label: "Seats", value: room ? `${(room.players || []).length}/4` : "4" },
      { label: "Min Entry", value: "1.00 USDT" },
      { label: "Wallet", value: `${balance.toFixed(2)} USDT` },
    ],
    [balance, room],
  );

  const leaveRoom = async () => {
    if (!room || !user?.uid) {
      setCurrentRoomId(null);
      return;
    }

    if (room.status !== "waiting") {
      setCurrentRoomId(null);
      return;
    }

    try {
      await runTransaction(db, async (transaction) => {
        const roomRef = doc(db, "omi_rooms", room.id);
        const roomSnap = await transaction.get(roomRef);
        if (!roomSnap.exists()) return;

        const roomData = roomSnap.data() || {};
        const players = [...(roomData.players || [])];
        const me = players.find((player) => player.userId === user.uid);
        if (!me) return;

        const nextPlayers = players
          .filter((player) => player.userId !== user.uid)
          .map((player, index) => ({ ...player, seat: index + 1 }));

        const userRef = doc(db, "users", user.uid);
        const userSnap = await transaction.get(userRef);
        if (userSnap.exists()) {
          const currentBalance = Number(userSnap.data()?.balance || 0);
          transaction.update(userRef, {
            balance: Number((currentBalance + Number(roomData.betAmount || 0)).toFixed(2)),
            activeOmiRoomId: deleteField(),
          });
        }

        if (!nextPlayers.length) {
          transaction.delete(roomRef);
          return;
        }

        transaction.update(roomRef, {
          players: nextPlayers,
          createdBy: nextPlayers[0],
        });
      });

      setCurrentRoomId(null);
      toast.success("Left the Omi table and refunded your entry.");
    } catch (error) {
      toast.error(error?.message || "Failed to leave the table.");
    }
  };

  const leaveMatch = async () => {
    if (!room || !user?.uid) {
      setCurrentRoomId(null);
      return;
    }

    if (room.status === "finished") {
      try {
        await runTransaction(db, async (transaction) => {
          const userRef = doc(db, "users", user.uid);
          const userSnap = await transaction.get(userRef);
          if (!userSnap.exists()) return;
          transaction.update(userRef, { activeOmiRoomId: deleteField() });
        });
      } catch {
        // no-op; UI can still navigate away locally
      }
      setCurrentRoomId(null);
      return;
    }

    const confirmed = window.confirm(
      "Leaving now will forfeit the match for your team. Do you want to continue?",
    );
    if (!confirmed) return;

    try {
      await runTransaction(db, async (transaction) => {
        const roomRef = doc(db, "omi_rooms", room.id);
        const roomSnap = await transaction.get(roomRef);
        if (!roomSnap.exists()) throw new Error("Room not found.");

        const roomData = roomSnap.data() || {};
        if (roomData.status !== "playing") return;
        if (roomData.payoutProcessed) return;

        const players = roomData.players || [];
        const leavingPlayer = players.find((player) => player.userId === user.uid);
        if (!leavingPlayer) return;

        const winnerTeam = getOpposingTeam(getTeamKeyFromSeat(Number(leavingPlayer.seat || 1)));
        const winners = players.filter(
          (player) => getTeamKeyFromSeat(Number(player.seat || 1)) === winnerTeam,
        );
        const betAmount = Number(roomData.betAmount || 0);
        const pot = betAmount * players.length;
        const payoutPool = Number((pot * 0.9).toFixed(2));
        const splitPayout = winners.length
          ? Number((payoutPool / winners.length).toFixed(2))
          : 0;

        for (const player of winners) {
          const playerRef = doc(db, "users", player.userId);
          const playerSnap = await transaction.get(playerRef);
          if (!playerSnap.exists()) continue;
          transaction.update(playerRef, {
            balance: Number((Number(playerSnap.data()?.balance || 0) + splitPayout).toFixed(2)),
          });
        }

        const leavingUserRef = doc(db, "users", user.uid);
        const leavingUserSnap = await transaction.get(leavingUserRef);
        if (leavingUserSnap.exists()) {
          transaction.update(leavingUserRef, { activeOmiRoomId: deleteField() });
        }

        transaction.update(roomRef, {
          status: "finished",
          winnerTeam,
          payoutProcessed: true,
          finishedReason: "forfeit",
          forfeitedBy: user.uid,
          rematchReady: [],
          updatedAt: serverTimestamp(),
          finishedAt: serverTimestamp(),
        });
      });

      toast.success("Match forfeited. The opposite team has been paid out.");
      setCurrentRoomId(null);
    } catch (error) {
      toast.error(error?.message || "Failed to leave the Omi match.");
    }
  };

  return (
    <div className="min-h-screen bg-[#171b22] px-3 py-5 text-white md:mt-16 sm:px-5 lg:px-6">
      <div className="mx-auto max-w-[1400px] space-y-4">
        <section className="rounded-[30px] bg-[#1f242d] p-5 shadow-[0_24px_70px_rgba(0,0,0,0.28)]">
          <div className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-emerald-300">
                Classic Card Table
              </div>
              <h1 className="mt-3 text-4xl font-black text-white sm:text-5xl">
                Omi Multiplayer
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-8 text-slate-400">
                Create a four-seat house, share the room code, and play a live card
                table with team scoring and wallet stakes.
              </p>

              {!currentRoomId ? (
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreate(true)}
                    className="rounded-[18px] bg-emerald-400 px-5 py-3 text-sm font-black text-[#05120d]"
                  >
                    Create Table
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowJoin(true)}
                    className="rounded-[18px] bg-[#2a3039] px-5 py-3 text-sm font-bold text-white"
                  >
                    Join Table
                  </button>
                </div>
              ) : null}
            </div>

            <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
              {summaryStats.map((item) => (
                <div key={item.label} className="rounded-[20px] bg-[#181d25] px-4 py-4">
                  <div className="text-xs uppercase tracking-[0.24em] text-slate-500">
                    {item.label}
                  </div>
                  <div className="mt-2 text-2xl font-black text-white">{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {currentRoomId && room ? (
          isPlaying ? (
            <OmiGameRoom room={room} user={user} onLeave={leaveMatch} />
          ) : (
            <OmiRoomWaiting room={room} user={user} onLeave={leaveRoom} />
          )
        ) : (
          <section className="rounded-[30px] bg-[#1f242d] p-5 shadow-[0_24px_70px_rgba(0,0,0,0.28)]">
            <div className="grid gap-4 lg:grid-cols-3">
              {[
                {
                  title: "Create House",
                  copy: "Open a private Omi table with your own entry fee and room code.",
                },
                {
                  title: "Join by Code",
                  copy: "Jump into a friend’s house instantly with the shared table code.",
                },
                {
                  title: "4-Seat Match",
                  copy: "Seats 1 and 3 play as one team, while seats 2 and 4 form the other side.",
                },
              ].map((item) => (
                <div key={item.title} className="rounded-[24px] bg-[#181d25] p-5">
                  <div className="text-xl font-black text-white">{item.title}</div>
                  <div className="mt-3 text-sm leading-7 text-slate-400">{item.copy}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <OmiCreateRoomModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        user={user}
        setCurrentRoomId={setCurrentRoomId}
      />
      <OmiJoinRoomModal
        open={showJoin}
        onClose={() => setShowJoin(false)}
        user={user}
        setCurrentRoomId={setCurrentRoomId}
      />
    </div>
  );
}
